// src/services/enhancedFirestore.js
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  addDoc,
  updateDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  startAfter,
  deleteDoc,
  serverTimestamp,
  onSnapshot,
  arrayUnion,
  arrayRemove,
  increment,
  writeBatch,
  getFirestore
} from "firebase/firestore";
import { db, auth, app } from "./firebase";
  
  // ========== REVIEWS & RATINGS SYSTEM ==========
  
  // Add a review for a provider
  export const addProviderReview = async (providerId, parentId, reviewData) => {
    try {
      const reviewsRef = collection(db, "providerReviews");
      const newReview = {
        providerId,
        parentId,
        rating: reviewData.rating,
        comment: reviewData.comment,
        createdAt: serverTimestamp(),
        status: "active" // Can be used for moderation if needed
      };
      
      // Check if user has already left a review for this provider
      const q = query(
        reviewsRef, 
        where("providerId", "==", providerId),
        where("parentId", "==", parentId)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        // Update existing review
        const reviewDoc = querySnapshot.docs[0];
        await updateDoc(doc(db, "providerReviews", reviewDoc.id), {
          rating: reviewData.rating,
          comment: reviewData.comment,
          updatedAt: serverTimestamp()
        });
        
        // Update provider average rating
        await updateProviderRating(providerId);
        
        return { id: reviewDoc.id, ...reviewData };
      } else {
        // Create new review
        const docRef = await addDoc(reviewsRef, newReview);
        
        // Update provider average rating
        await updateProviderRating(providerId);
        
        return { id: docRef.id, ...newReview };
      }
    } catch (error) {
      throw error;
    }
  };
  
  // Get all reviews for a provider
  export const getProviderReviews = async (providerId) => {
    try {
      const reviewsRef = collection(db, "providerReviews");
      const q = query(
        reviewsRef, 
        where("providerId", "==", providerId),
        where("status", "==", "active"),
        orderBy("createdAt", "desc")
      );
      
      const querySnapshot = await getDocs(q);
      const reviews = [];
      
      querySnapshot.forEach((doc) => {
        reviews.push({ id: doc.id, ...doc.data() });
      });
      
      return reviews;
    } catch (error) {
      throw error;
    }
  };
  
  // Update provider average rating
  export const updateProviderRating = async (providerId) => {
    try {
      const reviewsRef = collection(db, "providerReviews");
      const q = query(
        reviewsRef, 
        where("providerId", "==", providerId),
        where("status", "==", "active")
      );
      
      const querySnapshot = await getDocs(q);
      let totalRating = 0;
      const reviews = [];
      
      querySnapshot.forEach((doc) => {
        const reviewData = doc.data();
        totalRating += reviewData.rating;
        reviews.push({ id: doc.id, ...reviewData });
      });
      
      const averageRating = reviews.length > 0 ? totalRating / reviews.length : 0;
      
      // Update provider document with average rating and review count
      const providerRef = doc(db, "providers", providerId);
      await updateDoc(providerRef, { 
        averageRating: averageRating,
        reviewCount: reviews.length
      });
      
      return { averageRating, reviewCount: reviews.length };
    } catch (error) {
      throw error;
    }
  };
  
  // Add a review for a job seeker
  export const addJobSeekerReview = async (jobSeekerId, providerId, reviewData) => {
    try {
      const reviewsRef = collection(db, "jobSeekerReviews");
      const newReview = {
        jobSeekerId,
        providerId,
        rating: reviewData.rating,
        comment: reviewData.comment,
        createdAt: serverTimestamp(),
        status: "active"
      };
      
      // Check if provider has already left a review for this job seeker
      const q = query(
        reviewsRef, 
        where("jobSeekerId", "==", jobSeekerId),
        where("providerId", "==", providerId)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        // Update existing review
        const reviewDoc = querySnapshot.docs[0];
        await updateDoc(doc(db, "jobSeekerReviews", reviewDoc.id), {
          rating: reviewData.rating,
          comment: reviewData.comment,
          updatedAt: serverTimestamp()
        });
        
        // Update job seeker average rating
        await updateJobSeekerRating(jobSeekerId);
        
        return { id: reviewDoc.id, ...reviewData };
      } else {
        // Create new review
        const docRef = await addDoc(reviewsRef, newReview);
        
        // Update job seeker average rating
        await updateJobSeekerRating(jobSeekerId);
        
        return { id: docRef.id, ...newReview };
      }
    } catch (error) {
      throw error;
    }
  };
  
  // Get all reviews for a job seeker
  export const getJobSeekerReviews = async (jobSeekerId) => {
    try {
      const reviewsRef = collection(db, "jobSeekerReviews");
      const q = query(
        reviewsRef, 
        where("jobSeekerId", "==", jobSeekerId),
        where("status", "==", "active"),
        orderBy("createdAt", "desc")
      );
      
      const querySnapshot = await getDocs(q);
      const reviews = [];
      
      querySnapshot.forEach((doc) => {
        reviews.push({ id: doc.id, ...doc.data() });
      });
      
      return reviews;
    } catch (error) {
      throw error;
    }
  };
  
  // Update job seeker average rating
  export const updateJobSeekerRating = async (jobSeekerId) => {
    try {
      const reviewsRef = collection(db, "jobSeekerReviews");
      const q = query(
        reviewsRef, 
        where("jobSeekerId", "==", jobSeekerId),
        where("status", "==", "active")
      );
      
      const querySnapshot = await getDocs(q);
      let totalRating = 0;
      const reviews = [];
      
      querySnapshot.forEach((doc) => {
        const reviewData = doc.data();
        totalRating += reviewData.rating;
        reviews.push({ id: doc.id, ...reviewData });
      });
      
      const averageRating = reviews.length > 0 ? totalRating / reviews.length : 0;
      
      // Update job seeker document with average rating and review count
      const jobSeekerRef = doc(db, "jobseekers", jobSeekerId);
      await updateDoc(jobSeekerRef, { 
        averageRating: averageRating,
        reviewCount: reviews.length
      });
      
      return { averageRating, reviewCount: reviews.length };
    } catch (error) {
      throw error;
    }
  };
  
  // ========== NOTIFICATION SYSTEM ==========
  
  // Create a new notification
  export const createNotification = async (userId, notificationData) => {
    try {
      const notificationsRef = collection(db, "notifications");
      const newNotification = {
        userId,
        title: notificationData.title,
        message: notificationData.message,
        type: notificationData.type,
        read: false,
        relatedDocId: notificationData.relatedDocId || null,
        createdAt: serverTimestamp()
      };
      
      const docRef = await addDoc(notificationsRef, newNotification);
      return { id: docRef.id, ...newNotification };
    } catch (error) {
      throw error;
    }
  };
  
  // Get all notifications for a user
  export const getUserNotifications = async (userId, limitCount = 50) => {
    try {
      const notificationsRef = collection(db, "notifications");
      const q = query(
        notificationsRef, 
        where("userId", "==", userId),
        orderBy("createdAt", "desc"),
        limit(limitCount)  // Use the Firebase limit function with our renamed parameter
      );
      
      const querySnapshot = await getDocs(q);
      const notifications = [];
      
      querySnapshot.forEach((doc) => {
        notifications.push({ id: doc.id, ...doc.data() });
      });
      
      return notifications;
    } catch (error) {
      throw error;
    }
  };
  
  // Mark notification as read
  export const markNotificationAsRead = async (notificationId) => {
    try {
      const notificationRef = doc(db, "notifications", notificationId);
      await updateDoc(notificationRef, { 
        read: true,
        readAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      throw error;
    }
  };
  
  // Mark all notifications as read
  export const markAllNotificationsAsRead = async (userId) => {
    try {
      const notificationsRef = collection(db, "notifications");
      const q = query(
        notificationsRef, 
        where("userId", "==", userId),
        where("read", "==", false)
      );
      
      const querySnapshot = await getDocs(q);
      const batch = writeBatch(db);
      
      querySnapshot.forEach((document) => {
        const docRef = doc(db, "notifications", document.id);
        batch.update(docRef, { 
          read: true,
          readAt: serverTimestamp()
        });
      });
      
      await batch.commit();
      return true;
    } catch (error) {
      throw error;
    }
  };
  
  // Delete a notification
  export const deleteNotification = async (notificationId) => {
    try {
      await deleteDoc(doc(db, "notifications", notificationId));
      return true;
    } catch (error) {
      throw error;
    }
  };
  
  // Create notification for new spot matching parent criteria
  export const createSpotMatchNotification = async (spot, childAgeCategory) => {
    try {
      // Find parents who match the criteria
      const parentsRef = collection(db, "parents");
      let q = query(parentsRef);
      
      // Get all parents - we'll filter by criteria in the client
      // In a production environment, this would be refined with more specific queries
      const querySnapshot = await getDocs(q);
      
      const batch = writeBatch(db);
      const notificationsRef = collection(db, "notifications");
      
      querySnapshot.forEach((document) => {
        const parentData = document.data();
        const parentId = document.id;
        
        // Check if parent has a child in the relevant age category
        const hasMatchingChild = parentData.profile?.children?.some(child => {
          const childAge = parseInt(child.age);
          if (childAgeCategory === "Infant/Toddler") {
            return childAge < 2.5; // Under 30 months
          } else if (childAgeCategory === "Preschool") {
            return childAge >= 2.5 && childAge < 6; // 30 months to kindergarten
          } else if (childAgeCategory === "School-Age") {
            return childAge >= 6 && childAge <= 13; // Grade 1-13 years
          }
          return false;
        });
        
        if (hasMatchingChild) {
          const newNotification = {
            userId: parentId,
            title: "New Childcare Spot Available",
            message: `A new ${childAgeCategory} spot has opened up at ${spot.providerName}`,
            type: "spot_match",
            read: false,
            relatedDocId: spot.id,
            createdAt: serverTimestamp()
          };
          
          const newDocRef = doc(notificationsRef);
          batch.set(newDocRef, newNotification);
        }
      });
      
      await batch.commit();
      return true;
    } catch (error) {
      throw error;
    }
  };
  
  // ========== MESSAGING SYSTEM ==========
  
  // Create a new conversation or get existing one
export const getOrCreateConversation = async (user1Id, user2Id) => {
  try {
    // Input validation with detailed error messages
    if (!user1Id || !user2Id) {
      const error = new Error("Invalid user IDs: Both users must be specified");
      console.error("getOrCreateConversation validation error:", { user1Id, user2Id, error });
      throw error;
    }
    
    // Prevent starting a conversation with yourself - enhanced validation
    if (user1Id === user2Id) {
      const error = new Error("Cannot start a conversation with yourself.");
      console.error("getOrCreateConversation validation error: Self-conversation attempt", { 
        user1Id, 
        user2Id, 
        areEqual: user1Id === user2Id 
      });
      throw error;
    }
    
    console.log("Checking for existing conversation between users:", user1Id, "and", user2Id);
    
    // Search existing conversations
    const conversationsRef = collection(db, "conversations");
    const q = query(
      conversationsRef, 
      where("participants", "array-contains", user1Id)
    );
    
    const querySnapshot = await getDocs(q);
    console.log(`Found ${querySnapshot.size} conversations containing user ${user1Id}`);
    
    // Find conversation that includes the other user
    let existingConv = null;
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.participants && data.participants.includes(user2Id)) {
        console.log(`Found existing conversation ${doc.id} between users`);
        existingConv = { id: doc.id, ...data };
      }
    });
    
    if (existingConv) {
      console.log("Using existing conversation:", existingConv);
      return existingConv;
    }
    
    // Create new conversation since none exists
    console.log("No existing conversation found, creating new one");
    
    // Ensure participants array is sorted for consistency
    const participants = [user1Id, user2Id].sort();
    
    // Add member information
    const user1Doc = await getDoc(doc(db, "users", user1Id));
    const user2Doc = await getDoc(doc(db, "users", user2Id));
    
    const members = {};
    if (user1Doc.exists()) {
      members[user1Id] = {
        displayName: user1Doc.data().displayName || "Unknown User",
        photoURL: user1Doc.data().photoURL || null
      };
    } else {
      console.warn(`User document for ${user1Id} not found`);
    }
    
    if (user2Doc.exists()) {
      members[user2Id] = {
        displayName: user2Doc.data().displayName || "Unknown User",
        photoURL: user2Doc.data().photoURL || null
      };
    } else {
      console.warn(`User document for ${user2Id} not found`);
    }
    
    // Create participantMap for additional metadata
    const participantMap = {};
    const timestamp = serverTimestamp();
    
    participants.forEach(userId => {
      participantMap[userId] = {
        active: true,
        joinedAt: timestamp,
        lastSeen: timestamp,
        isTyping: false,
        hasRead: true
      };
    });
    
    const newConversation = {
      participants, // Keep array for backward compatibility
      participantMap, // Add the map for new features
      members,
      lastMessage: null,
      lastMessageTimestamp: null,
      createdAt: timestamp,
      updatedAt: timestamp
    };
    
    console.log("Creating new conversation with data:", newConversation);
    
    try {
      const docRef = await addDoc(conversationsRef, newConversation);
      console.log("New conversation created with ID:", docRef.id);
      
      // Get the complete document to return
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { id: docRef.id, ...docSnap.data() };
      } else {
        return { id: docRef.id, ...newConversation };
      }
    } catch (error) {
      console.error("Error adding conversation to Firestore:", error);
      console.error("Error code:", error.code);
      console.error("Error message:", error.message);
      throw error;
    }
  } catch (error) {
    console.error("Error in getOrCreateConversation:", error);
    throw error;
  }
};
// Update the sendMessage function in enhancedFirestore.js

// Send a message with enhanced error handling
export const sendMessage = async (conversationId, senderId, messageText) => {
  try {
    if (!conversationId) {
      throw new Error("Cannot send message: Conversation ID is missing");
    }
    
    if (!senderId) {
      throw new Error("Cannot send message: Sender ID is missing");
    }
    
    if (!messageText || messageText.trim() === '') {
      throw new Error("Cannot send empty message");
    }
    
    console.log(`Sending message in conversation ${conversationId} from ${senderId}: ${messageText}`);
    
    // Fetch the conversation to get participants
    const conversationRef = doc(db, "conversations", conversationId);
    const conversationSnap = await getDoc(conversationRef);
    
    if (!conversationSnap.exists()) {
      throw new Error("Conversation not found");
    }
    
    const conversationData = conversationSnap.data();
    const participants = conversationData.participants || [];
    
    // Validate that sender is a participant
    if (!participants.includes(senderId)) {
      throw new Error("Sender is not a participant in this conversation");
    }
    
    // Create participantMap for the message
    const messageParticipantMap = {};
    
    participants.forEach(userId => {
      messageParticipantMap[userId] = {
        hasRead: userId === senderId, // Sender has read the message
        readAt: userId === senderId ? serverTimestamp() : null
      };
    });
    
    // Add message to messages collection
    const messagesRef = collection(db, "messages");
    const newMessage = {
      conversationId,
      senderId,
      text: messageText,
      createdAt: serverTimestamp(),
      // Keep array for backward compatibility with existing indexes
      participants,
      // Add map for new features
      participantMap: messageParticipantMap,
      // Legacy boolean field
      read: false
    };
    
    console.log("Creating new message with data:", {
      conversationId,
      senderId,
      messageText,
      participants
    });
    
    try {
      const docRef = await addDoc(messagesRef, newMessage);
      console.log("Message added with ID:", docRef.id);
      
      // Update conversation document
      const conversationUpdates = {
        lastMessage: messageText,
        lastMessageTimestamp: serverTimestamp(),
        lastSenderId: senderId,
        updatedAt: serverTimestamp(),
      };
      
      // Update read status and typing status in conversation
      participants.forEach(userId => {
        if (userId !== senderId) {
          // Update in the participantMap if it exists
          if (conversationData.participantMap && conversationData.participantMap[userId]) {
            conversationUpdates[`participantMap.${userId}.hasRead`] = false;
          }
          
          // Create notification for recipient
          createNotification(userId, {
            title: "New Message",
            message: messageText.length > 50 ? messageText.substring(0, 47) + "..." : messageText,
            type: "message",
            relatedDocId: conversationId,
            link: `/messages?conversation=${conversationId}`
          }).catch(error => {
            console.error("Error creating notification:", error);
          });
        }
        if (userId === senderId) {
          // Update sender metadata in the participantMap if it exists
          if (conversationData.participantMap && conversationData.participantMap[userId]) {
            conversationUpdates[`participantMap.${userId}.isTyping`] = false;
            conversationUpdates[`participantMap.${userId}.lastSeen`] = serverTimestamp();
          }
        }
      });
      
      console.log("Updating conversation with data:", conversationUpdates);
      await updateDoc(conversationRef, conversationUpdates);
      
      // Return a structured message object that doesn't rely on Firestore timestamps
      return { 
        id: docRef.id, 
        ...newMessage,
        createdAt: new Date() // Use a JS Date object instead of Firestore timestamp for immediate use
      };
    } catch (error) {
      console.error("Error adding message to Firestore:", error);
      if (error.code === 'permission-denied') {
        throw new Error("Permission denied: Check your security rules for messages collection");
      }
      throw error;
    }
  } catch (error) {
    console.error("Error sending message:", error);
    throw error;
  }
};
// Add this to enhancedFirestore.js

// Ensure user document exists in Firestore
// Replace the ensureUserExists function in enhancedFirestore.js

// Ensure user document exists in Firestore
export const ensureUserExists = async (userId, userData = null) => {
  try {
    if (!userId) {
      throw new Error("User ID is required to ensure user exists");
    }
    
    // Check if user document exists
    const userRef = doc(db, "users", userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      console.log(`Creating missing user document for ${userId}`);
      
      // Get current user info - try from auth first, then fallback
      let currentUser = null;
      try {
        currentUser = auth.currentUser;
      } catch (err) {
        console.warn("Auth not available or no current user");
      }
      
      // Create basic user data
      const defaultUserData = {
        uid: userId,
        email: (currentUser?.email || userData?.email || "unknown@example.com"),
        displayName: (currentUser?.displayName || userData?.displayName || "Unknown User"),
        photoURL: (currentUser?.photoURL || userData?.photoURL || null),
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp()
      };
      
      // Merge with provided data if any
      const dataToSave = userData ? { ...defaultUserData, ...userData } : defaultUserData;
      
      // Save to Firestore
      await setDoc(userRef, dataToSave);
      
      return true;
    }
    
    return true;
  } catch (error) {
    console.error("Error ensuring user exists:", error);
    return false;
  }
};

// Call this before starting a conversation
export const prepareConversation = async (userId1, userId2) => {
  try {
    // Make sure both users exist
    await ensureUserExists(userId1);
    await ensureUserExists(userId2);
    
    return true;
  } catch (error) {
    console.error("Error preparing conversation:", error);
    return false;
  }
};
  
// Get all messages for a conversation with enhanced user info
export const getConversationMessages = async (conversationId, limitNum = 50) => {
  try {
    // First get the conversation to get member info
    const conversationRef = doc(db, "conversations", conversationId);
    const conversationSnap = await getDoc(conversationRef);
    
    // Get conversation data if it exists
    const conversationData = conversationSnap.exists() ? conversationSnap.data() : null;
    const memberInfo = conversationData?.members || {};
    
    // Now get the messages
    const messagesRef = collection(db, "messages");
    const q = query(
      messagesRef, 
      where("conversationId", "==", conversationId),
      orderBy("createdAt", "desc"),
      limit(limitNum)
    );
    
    const querySnapshot = await getDocs(q);
    const messages = [];
    
    // Enhanced message processing with sender info
    querySnapshot.forEach((doc) => {
      const messageData = doc.data();
      
      // Enhance message with sender info if available
      if (messageData.senderId && memberInfo[messageData.senderId]) {
        messages.push({ 
          id: doc.id, 
          ...messageData,
          // Add sender name directly to the message
          senderName: memberInfo[messageData.senderId].displayName || null,
          // Also include the entire members object for reference
          conversationMembers: memberInfo
        });
      } else {
        messages.push({ id: doc.id, ...messageData });
      }
    });
    
    // If we still don't have sender info, try to get it for each unique sender
    const sendersWithoutInfo = new Set();
    messages.forEach(message => {
      if (message.senderId && !message.senderName) {
        sendersWithoutInfo.add(message.senderId);
      }
    });
    
    // If there are senders without info, try to look them up
    if (sendersWithoutInfo.size > 0) {
      for (const senderId of sendersWithoutInfo) {
        try {
          const userRef = doc(db, "users", senderId);
          const userSnap = await getDoc(userRef);
          
          if (userSnap.exists()) {
            const userData = userSnap.data();
            const senderName = userData.displayName || null;
            
            // Update all messages from this sender
            messages.forEach(message => {
              if (message.senderId === senderId && !message.senderName) {
                message.senderName = senderName;
              }
            });
          }
        } catch (err) {
          console.warn(`Failed to look up user info for sender ${senderId}:`, err);
        }
      }
    }
    
    return messages.reverse(); // Return in chronological order
  } catch (error) {
    console.error("Error getting conversation messages:", error);
    throw error;
  }
};
  
  // Get all conversations for a user
  // Replace this function in enhancedFirestore.js
// Get user information by user ID
export const getUserInfo = async (userId) => {
  try {
    if (!userId) {
      throw new Error("User ID is required to get user information");
    }
    
    console.log("Getting user info for userId:", userId);
    
    // First try to get from the users collection
    const userRef = doc(db, "users", userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      console.log("Found user in users collection:", userData);
      return userData;
    }
    
    // If not found in users, try each user type collection
    const userTypes = ['parents', 'providers', 'jobseekers'];
    
    for (const userType of userTypes) {
      const typeRef = doc(db, userType, userId);
      const typeDoc = await getDoc(typeRef);
      
      if (typeDoc.exists()) {
        const typeData = typeDoc.data();
        console.log(`Found user in ${userType} collection:`, typeData);
        return typeData;
      }
    }
    
    console.warn("User not found in any collection:", userId);
    return null;
  } catch (error) {
    console.error("Error getting user info:", error);
    return null;
  }
};
// Get all conversations for a user
export const getUserConversations = async (userId) => {
  try {
    if (!userId) {
      console.error("getUserConversations: userId is null or undefined");
      throw new Error("User ID is required to fetch conversations");
    }
    
    console.log("getUserConversations called for userId:", userId);
    
    const conversationsRef = collection(db, "conversations");
    
    // Query for conversations where user is a participant
    // This approach only requires a single-field index on participants
    const q = query(
      conversationsRef, 
      where("participants", "array-contains", userId)
    );
    
    console.log("Executing Firestore query for conversations...");
    const querySnapshot = await getDocs(q);
    console.log(`Found ${querySnapshot.size} conversations for user ${userId}`);
    
    const conversations = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      console.log("Processing conversation:", doc.id);
      console.log("Conversation data:", data);
      
      // Ensure participants array exists
      if (!data.participants || !Array.isArray(data.participants)) {
        console.warn(`Conversation ${doc.id} has invalid participants data:`, data.participants);
        // Skip this conversation
        return;
      }
      
      // Verify current user is actually in participants
      if (!data.participants.includes(userId)) {
        console.warn(`Current user ${userId} not found in participants array for conversation ${doc.id}:`, data.participants);
        // Skip this conversation
        return;
      }
      
      conversations.push({ id: doc.id, ...data });
    });
    
    // Sort manually in JavaScript rather than in the query
    conversations.sort((a, b) => {
      // Sort by updatedAt in descending order
      const aTime = a.updatedAt ? new Date(a.updatedAt.seconds * 1000) : new Date(0);
      const bTime = b.updatedAt ? new Date(b.updatedAt.seconds * 1000) : new Date(0);
      return bTime - aTime;
    });
    
    return conversations;
  } catch (error) {
    console.error("Error getting user conversations:", error);
    console.error("Error details:", error.code, error.message);
    throw error;
  }
};
  
  // Mark all messages in a conversation as read
  export const markConversationAsRead = async (conversationId, userId) => {
    try {
      // First get the conversation to check if it has participantMap
      const conversationRef = doc(db, "conversations", conversationId);
      const conversationSnap = await getDoc(conversationRef);
      
      if (conversationSnap.exists()) {
        const conversationData = conversationSnap.data();
        
        // Update conversation read status if it has participantMap
        if (conversationData.participantMap && conversationData.participantMap[userId]) {
          await updateDoc(conversationRef, {
            [`participantMap.${userId}.hasRead`]: true,
            [`participantMap.${userId}.lastSeen`]: serverTimestamp()
          });
        }
      }
      
      // Now update the messages
      const messagesRef = collection(db, "messages");
      const q = query(
        messagesRef, 
        where("conversationId", "==", conversationId),
        where("senderId", "!=", userId),
        where("read", "==", false)
      );
      
      const querySnapshot = await getDocs(q);
      const batch = writeBatch(db);
      
      querySnapshot.forEach((document) => {
        const docRef = doc(db, "messages", document.id);
        const updateData = { 
          read: true, // Update legacy field
          readAt: serverTimestamp()
        };
        
        // Also update in participantMap if it exists
        const messageData = document.data();
        if (messageData.participantMap && messageData.participantMap[userId]) {
          updateData[`participantMap.${userId}.hasRead`] = true;
          updateData[`participantMap.${userId}.readAt`] = serverTimestamp();
        }
        
        batch.update(docRef, updateData);
      });
      
      await batch.commit();
      return true;
    } catch (error) {
      throw error;
    }
  };
  
  // Update typing status
  export const updateTypingStatus = async (conversationId, userId, isTyping) => {
    try {
      const conversationRef = doc(db, "conversations", conversationId);
      const conversationSnap = await getDoc(conversationRef);
      
      if (!conversationSnap.exists()) {
        throw new Error("Conversation not found");
      }
      
      const conversationData = conversationSnap.data();
      
      // Only update if the conversation has a participantMap
      if (conversationData.participantMap && conversationData.participantMap[userId]) {
        await updateDoc(conversationRef, {
          [`participantMap.${userId}.isTyping`]: isTyping,
          [`participantMap.${userId}.lastSeen`]: serverTimestamp()
        });
      }
      
      return true;
    } catch (error) {
      console.error("Error updating typing status:", error);
      throw error;
    }
  };
  
  // Get unread message count for a user
  export const getUnreadMessageCount = async (userId) => {
    try {
      const conversationsRef = collection(db, "conversations");
      const q = query(
        conversationsRef, 
        where("participants", "array-contains", userId)
      );
      
      const querySnapshot = await getDocs(q);
      let totalUnread = 0;
      
      for (const document of querySnapshot.docs) {
        const conversationId = document.id;
        const messagesRef = collection(db, "messages");
        const mq = query(
          messagesRef, 
          where("conversationId", "==", conversationId),
          where("senderId", "!=", userId),
          where("read", "==", false)
        );
        
        const messageSnapshot = await getDocs(mq);
        totalUnread += messageSnapshot.size;
      }
      
      return totalUnread;
    } catch (error) {
      throw error;
    }
  };
  
  // Migration function to add participantMap to existing conversations and messages
  export const migrateMessagingToParticipantMap = async (batchSize = 100) => {
    try {
      const results = {
        conversationsProcessed: 0,
        messagesProcessed: 0,
        errors: []
      };
      
      // Step 1: Migrate conversations
      const conversationsRef = collection(db, "conversations");
      const conversationsQuery = query(
        conversationsRef,
        limit(batchSize)
      );
      
      const conversationsSnapshot = await getDocs(conversationsQuery);
      const conversationBatch = writeBatch(db);
      let batchCount = 0;
      
      for (const convDoc of conversationsSnapshot.docs) {
        try {
          const convData = convDoc.data();
          
          // Skip if already has participantMap
          if (convData.participantMap) continue;
          
          // Create participantMap from participants array
          if (convData.participants && Array.isArray(convData.participants)) {
            const participantMap = {};
            const timestamp = convData.createdAt || serverTimestamp();
            
            convData.participants.forEach(userId => {
              participantMap[userId] = {
                active: true,
                joinedAt: timestamp,
                lastSeen: timestamp,
                isTyping: false,
                hasRead: true
              };
            });
            
            conversationBatch.update(convDoc.ref, { participantMap });
            batchCount++;
            results.conversationsProcessed++;
          }
        } catch (error) {
          results.errors.push(`Error with conversation ${convDoc.id}: ${error.message}`);
        }
      }
      
      // Commit the batch if there are updates
      if (batchCount > 0) {
        await conversationBatch.commit();
      }
      
      // Step 2: Migrate messages
      const messagesRef = collection(db, "messages");
      const messagesQuery = query(
        messagesRef,
        limit(batchSize)
      );
      
      const messagesSnapshot = await getDocs(messagesQuery);
      const messageBatch = writeBatch(db);
      batchCount = 0;
      
      for (const msgDoc of messagesSnapshot.docs) {
        try {
          const msgData = msgDoc.data();
          
          // Skip if already has participantMap
          if (msgData.participantMap) continue;
          
          // Create participantMap from participants array
          if (msgData.participants && Array.isArray(msgData.participants)) {
            const participantMap = {};
            
            msgData.participants.forEach(userId => {
              participantMap[userId] = {
                hasRead: userId === msgData.senderId, // Sender has read the message
                readAt: userId === msgData.senderId ? msgData.createdAt : null
              };
            });
            
            messageBatch.update(msgDoc.ref, { participantMap });
            batchCount++;
            results.messagesProcessed++;
          }
        } catch (error) {
          results.errors.push(`Error with message ${msgDoc.id}: ${error.message}`);
        }
      }
      
      // Commit the batch if there are updates
      if (batchCount > 0) {
        await messageBatch.commit();
      }
      
      return results;
    } catch (error) {
      console.error("Migration error:", error);
      throw error;
    }
  };
  
  // Run this function repeatedly to migrate all data in batches
  export const migrateAllMessagingData = async (maxBatches = 10) => {
    let batchesProcessed = 0;
    let totalConversations = 0;
    let totalMessages = 0;
    const errors = [];
    
    try {
      while (batchesProcessed < maxBatches) {
        const results = await migrateMessagingToParticipantMap(100);
        
        totalConversations += results.conversationsProcessed;
        totalMessages += results.messagesProcessed;
        errors.push(...results.errors);
        
        // If no documents were processed in this batch, we're done
        if (results.conversationsProcessed === 0 && results.messagesProcessed === 0) {
          break;
        }
        
        batchesProcessed++;
      }
      
      return {
        batchesProcessed,
        totalConversationsProcessed: totalConversations,
        totalMessagesProcessed: totalMessages,
        complete: batchesProcessed < maxBatches,
        errors
      };
    } catch (error) {
      console.error("Migration error:", error);
      throw error;
    }
  };
  
  // ========== WAITLIST MANAGEMENT ==========
  
  // Update waitlist entry status
  export const updateWaitlistEntryStatus = async (entryId, status, notes = "") => {
    try {
      const entryRef = doc(db, "waitingList", entryId);
      await updateDoc(entryRef, { 
        status,
        statusUpdatedAt: serverTimestamp(),
        notes: notes || ""
      });
      
      // Get the updated document to return
      const updatedDoc = await getDoc(entryRef);
      return { id: updatedDoc.id, ...updatedDoc.data() };
    } catch (error) {
      throw error;
    }
  };
  
  // Move waitlist position (up or down)
  export const moveWaitlistEntry = async (providerId, entryId, direction) => {
    try {
      // Get the current entry
      const entryRef = doc(db, "waitingList", entryId);
      const entrySnap = await getDoc(entryRef);
      
      if (!entrySnap.exists()) {
        throw new Error("Waitlist entry not found");
      }
      
      const currentEntry = entrySnap.data();
      const currentPosition = currentEntry.position || 0;
      
      // Get all entries for this provider
      const waitingListRef = collection(db, "waitingList");
      const q = query(
        waitingListRef, 
        where("providerUid", "==", providerId),
        where("status", "==", "pending"),
        orderBy("position", "asc")
      );
      
      const querySnapshot = await getDocs(q);
      const entries = [];
      
      querySnapshot.forEach((doc) => {
        entries.push({ id: doc.id, ...doc.data() });
      });
      
      // Find the entry to swap positions with
      let swapIndex = -1;
      
      if (direction === "up" && currentPosition > 1) {
        // Find the entry with the next position up
        swapIndex = entries.findIndex(entry => entry.position === currentPosition - 1);
      } else if (direction === "down" && currentPosition < entries.length) {
        // Find the entry with the next position down
        swapIndex = entries.findIndex(entry => entry.position === currentPosition + 1);
      }
      
      if (swapIndex === -1) {
        return { success: false, message: "Cannot move entry further in that direction" };
      }
      
      // Swap positions
      const swapEntry = entries[swapIndex];
      const swapEntryRef = doc(db, "waitingList", swapEntry.id);
      
      const batch = writeBatch(db);
      
      // Update current entry position
      batch.update(entryRef, { 
        position: swapEntry.position,
        updatedAt: serverTimestamp()
      });
      
      // Update swap entry position
      batch.update(swapEntryRef, { 
        position: currentPosition,
        updatedAt: serverTimestamp()
      });
      
      await batch.commit();
      
      return { 
        success: true, 
        message: `Entry moved ${direction} successfully`
      };
      
    } catch (error) {
      throw error;
    }
  };
  
  // Get waitlist by age category
  export const getWaitlistByAgeCategory = async (providerId, ageCategory) => {
    try {
      const waitingListRef = collection(db, "waitingList");
      const q = query(
        waitingListRef, 
        where("providerUid", "==", providerId),
        where("childData.ageCategory", "==", ageCategory),
        orderBy("position", "asc")
      );
      
      const querySnapshot = await getDocs(q);
      const entries = [];
      
      querySnapshot.forEach((doc) => {
        entries.push({ id: doc.id, ...doc.data() });
      });
      
      return entries;
    } catch (error) {
      throw error;
    }
  };
  
  // Helper to calculate age from dob (YYYY-MM-DD or Date)
  function calculateAge(dob) {
    if (!dob) return null;
    let birthDate;
    if (typeof dob === 'string') {
      birthDate = new Date(dob);
    } else if (dob instanceof Date) {
      birthDate = dob;
    } else {
      return null;
    }
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }
  
  function getAgeCategory(age) {
    if (age === null || age === undefined || isNaN(age)) return null;
    if (age <= 2) return 'Infant/Toddler';
    if (age >= 3 && age <= 5) return 'Preschool';
    if (age >= 6) return 'School-Age';
    return null;
  }
  
  // Get waitlist statistics
  export const getWaitlistStatistics = async (providerId) => {
    try {
      const waitingListRef = collection(db, "waitingList");
      const q = query(
        waitingListRef, 
        where("providerUid", "==", providerId)
      );
      
      const querySnapshot = await getDocs(q);
      
      const stats = {
        total: 0,
        byStatus: {
          pending: 0,
          contacted: 0,
          offered: 0,
          accepted: 0,
          declined: 0,
          removed: 0
        },
        byAgeCategory: {
          "Infant/Toddler": 0,
          "Preschool": 0,
          "School-Age": 0
        }
      };
      
      querySnapshot.forEach((doc) => {
        const entry = doc.data();
        stats.total++;
        
        // Count by status
        if (stats.byStatus[entry.status] !== undefined) {
          stats.byStatus[entry.status]++;
        }
        
        // Count by calculated age category
        let ageCategory = null;
        if (entry.childData && entry.childData.dob) {
          const age = calculateAge(entry.childData.dob);
          ageCategory = getAgeCategory(age);
        } else if (entry.childData && entry.childData.ageCategory) {
          ageCategory = entry.childData.ageCategory;
        }
        if (ageCategory && stats.byAgeCategory[ageCategory] !== undefined) {
          stats.byAgeCategory[ageCategory]++;
        }
      });
      
      return stats;
    } catch (error) {
      throw error;
    }
  };
  
  // ========== VERIFICATION SYSTEM ==========
  
  // Submit verification for approval
  export const submitVerification = async (userId, verificationType, documentUrls) => {
    try {
      const verificationsRef = collection(db, "verifications");
      const newVerification = {
        userId,
        type: verificationType,
        documentUrls,
        status: "pending", // pending, approved, rejected
        submittedAt: serverTimestamp(),
        reviewedAt: null,
        reviewNotes: null
      };
      
      const docRef = await addDoc(verificationsRef, newVerification);
      
      // Update user document with pending verification
      const userDocRef = doc(db, "users", userId);
      await updateDoc(userDocRef, {
        pendingVerifications: arrayUnion(verificationType)
      });
      
      return { id: docRef.id, ...newVerification };
    } catch (error) {
      throw error;
    }
  };
  
  // Get user verifications
  export const getUserVerifications = async (userId) => {
    try {
      const verificationsRef = collection(db, "verifications");
      const q = query(
        verificationsRef, 
        where("userId", "==", userId),
        orderBy("submittedAt", "desc")
      );
      
      const querySnapshot = await getDocs(q);
      const verifications = [];
      
      querySnapshot.forEach((doc) => {
        verifications.push({ id: doc.id, ...doc.data() });
      });
      
      return verifications;
    } catch (error) {
      throw error;
    }
  };
  
  // For demonstration purposes - in a real app, this would be an admin function
  export const approveVerification = async (verificationId) => {
    try {
      const verificationRef = doc(db, "verifications", verificationId);
      const verificationSnap = await getDoc(verificationRef);
      
      if (!verificationSnap.exists()) {
        throw new Error("Verification not found");
      }
      
      const verification = verificationSnap.data();
      
      // Update verification status
      await updateDoc(verificationRef, {
        status: "approved",
        reviewedAt: serverTimestamp(),
        reviewNotes: "Verification approved"
      });
      
      // Update user document
      const userDocRef = doc(db, "users", verification.userId);
      await updateDoc(userDocRef, {
        verifications: arrayUnion(verification.type),
        pendingVerifications: arrayRemove(verification.type)
      });
      
      // Also update the specific user type document
      const userDoc = await getDoc(doc(db, "users", verification.userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const userTypeRef = doc(db, userData.userType, verification.userId);
        await updateDoc(userTypeRef, {
          verifications: arrayUnion(verification.type)
        });
      }
      
      return { success: true };
    } catch (error) {
      throw error;
    }
  };
  
  // For demonstration purposes
  export const rejectVerification = async (verificationId, reason) => {
    try {
      const verificationRef = doc(db, "verifications", verificationId);
      const verificationSnap = await getDoc(verificationRef);
      
      if (!verificationSnap.exists()) {
        throw new Error("Verification not found");
      }
      
      const verification = verificationSnap.data();
      
      // Update verification status
      await updateDoc(verificationRef, {
        status: "rejected",
        reviewedAt: serverTimestamp(),
        reviewNotes: reason || "Verification rejected"
      });
      
      // Update user document
      const userDocRef = doc(db, "users", verification.userId);
      await updateDoc(userDocRef, {
        pendingVerifications: arrayRemove(verification.type)
      });
      
      return { success: true };
    } catch (error) {
      throw error;
    }
  };

// Create notification for new job application
export const createJobApplicationNotification = async (application) => {
  try {
    const notificationData = {
      title: "New Job Application",
      message: `${application.jobSeekerName} has applied for your job: ${application.jobTitle}`,
      type: "job_application",
      relatedDocId: application.id,
      link: `/parent/applications` // or the correct link for parents
    };

    // Use parentUid for parent jobs
    await createNotification(application.parentUid, notificationData);
    return true;
  } catch (error) {
    console.error("Error creating job application notification:", error);
    return false;
  }
};